/**
 * «Когда удобно заниматься?» - структурированное время заявки (Figma «Подбор - шаг 6»).
 * Режимы (p4):
 *  - daily   - «Ежедневно»: одно время С-До на все 7 дней;
 *  - days    - «Одно время в выбранные дни»: дни + одно время (старый формат);
 *  - per_day - «Разное время по дням»: для каждого дня свой интервал (slots).
 * В API: study_time = {mode, days, time_from, time_to, slots?}. days/time_from/time_to заполнены всегда
 * (для per_day - дни слотов и общий min/max), поэтому старые клиенты, фильтры и коды study_schedule работают.
 * Старые заявки без mode читаются как days; совсем старые хранят только коды study_schedule.
 */

export type StudyMode = 'daily' | 'days' | 'per_day';
export type StudySlot = { day: string; time_from: string; time_to: string };

export type StudyTime = {
  mode?: StudyMode;
  days: string[];
  time_from: string;
  time_to: string;
  slots?: StudySlot[];
};

export const STUDY_DAYS: Array<{ value: string; label: string; full: string }> = [
  { value: 'mon', label: 'Пн', full: 'понедельник' },
  { value: 'tue', label: 'Вт', full: 'вторник' },
  { value: 'wed', label: 'Ср', full: 'среда' },
  { value: 'thu', label: 'Чт', full: 'четверг' },
  { value: 'fri', label: 'Пт', full: 'пятница' },
  { value: 'sat', label: 'Сб', full: 'суббота' },
  { value: 'sun', label: 'Вс', full: 'воскресенье' },
];

export const STUDY_MODES: Array<{ value: StudyMode; label: string }> = [
  { value: 'daily', label: 'Ежедневно' },
  { value: 'per_day', label: 'По дням' },
];

const DAY_ORDER = STUDY_DAYS.map((d) => d.value);
const WEEKDAYS = ['mon', 'tue', 'wed', 'thu', 'fri'];
const WEEKENDS = ['sat', 'sun'];
const TIME_RE = /^([01]\d|2[0-3]):([0-5]\d)$/;
const DASH = '–';

export const EMPTY_STUDY_TIME: StudyTime = { mode: 'per_day', days: [], time_from: '', time_to: '', slots: [] };

const dayLabel = (d: string) => STUDY_DAYS.find((x) => x.value === d)?.label || d;
const orderDays = (days: string[]) => DAY_ORDER.filter((d) => days.includes(d));

export function studyMode(time: StudyTime | null | undefined): StudyMode {
  const m = time?.mode;
  return m === 'daily' || m === 'per_day' ? m : 'days';
}

/**
 * p4: в интерфейсе только два режима - тогл «Ежедневно» (daily) и «по дням» (per_day).
 * Старый режим days («одно время в выбранные дни») и старый формат без mode открываются как per_day
 * с тем же временем у выбранных дней. Сервер по-прежнему принимает все форматы.
 */
export function normalizeStudyTime(time: StudyTime | null | undefined): StudyTime {
  const t = time || EMPTY_STUDY_TIME;
  if (t.mode === 'daily') return t;
  if (t.mode === 'per_day' && Array.isArray(t.slots)) return t;
  const days = orderDays(t.days || []);
  return syncDerived({
    mode: 'per_day',
    days,
    time_from: t.time_from,
    time_to: t.time_to,
    slots: days.map((d) => ({ day: d, time_from: t.time_from, time_to: t.time_to })),
  });
}

/** Тогл «Ежедневно»: вкл → одно время на все дни, выкл → время по дням. */
export function setStudyDaily(time: StudyTime | null | undefined, on: boolean): StudyTime {
  return setStudyMode(normalizeStudyTime(time), on ? 'daily' : 'per_day');
}

export function toggleStudyDay(time: StudyTime, day: string): StudyTime {
  const has = time.days.includes(day);
  const days = has ? time.days.filter((d) => d !== day) : [...time.days, day];
  return { ...time, days: orderDays(days) };
}

/** Слот дня в режиме per_day (или undefined, если день выключен). */
export function studySlot(time: StudyTime, day: string): StudySlot | undefined {
  return (time.slots || []).find((s) => s.day === day);
}

/** Вкл/выкл день в режиме per_day; новый день берёт время предыдущего включённого (удобно заполнять). */
export function toggleStudySlot(time: StudyTime, day: string): StudyTime {
  const slots = time.slots || [];
  if (slots.some((s) => s.day === day)) {
    return syncDerived({ ...time, slots: slots.filter((s) => s.day !== day) });
  }
  const last = slots[slots.length - 1];
  const next = [...slots, { day, time_from: last?.time_from || '', time_to: last?.time_to || '' }];
  return syncDerived({ ...time, slots: next });
}

export function patchStudySlot(time: StudyTime, day: string, patch: Partial<StudySlot>): StudyTime {
  const slots = (time.slots || []).map((s) => (s.day === day ? { ...s, ...patch } : s));
  return syncDerived({ ...time, slots });
}

/** days/time_from/time_to из слотов (per_day) или все 7 дней (daily). */
function syncDerived(time: StudyTime): StudyTime {
  const mode = studyMode(time);
  if (mode === 'per_day') {
    const slots = DAY_ORDER.map((d) => (time.slots || []).find((s) => s.day === d)).filter(
      Boolean,
    ) as StudySlot[];
    const froms = slots.map((s) => s.time_from).filter(isValidStudyTime).sort();
    const tos = slots.map((s) => s.time_to).filter(isValidStudyTime).sort();
    return {
      ...time,
      slots,
      days: slots.map((s) => s.day),
      time_from: froms[0] || '',
      time_to: tos[tos.length - 1] || '',
    };
  }
  if (mode === 'daily') return { ...time, days: [...DAY_ORDER], slots: undefined };
  return { ...time, slots: undefined };
}

/** Переключение режима с переносом уже введённого (дни/время не теряются). */
export function setStudyMode(time: StudyTime | null | undefined, mode: StudyMode): StudyTime {
  const t = time || EMPTY_STUDY_TIME;
  const cur = studyMode(t);
  if (cur === mode) return mode === 'days' ? t : normalizeStudyTime(t);
  if (mode === 'per_day') {
    const days = cur === 'daily' ? (t.time_from || t.time_to ? [...DAY_ORDER] : []) : t.days;
    return syncDerived({
      mode,
      days,
      time_from: t.time_from,
      time_to: t.time_to,
      slots: days.map((d) => ({ day: d, time_from: t.time_from, time_to: t.time_to })),
    });
  }
  const first = cur === 'per_day' ? (t.slots || [])[0] : undefined;
  const from = first ? first.time_from : t.time_from;
  const to = first ? first.time_to : t.time_to;
  if (mode === 'daily') return syncDerived({ mode, days: [], time_from: from, time_to: to });
  return { mode, days: cur === 'daily' ? [] : t.days, time_from: from, time_to: to };
}

/**
 * Маска «ЧЧ:ММ» для текстового поля (Figma: «__:__»). Неверную цифру ввести нельзя:
 * первая цифра часа 0-2, при «2» вторая 0-3, первая цифра минут 0-5 (иначе цифра отбрасывается).
 * Вставка «9:30» / «9.30» / «0930» → «09:30».
 */
export function maskStudyTime(raw: string): string {
  const src = String(raw || '');
  const sep = src.match(/^\s*(\d{1,2})\s*[:.\-ч]\s*(\d{0,2})/);
  let digits: string;
  if (sep) {
    const h = sep[1].length === 1 ? `0${sep[1]}` : sep[1];
    digits = `${h}${sep[2]}`;
  } else {
    digits = src.replace(/\D/g, '');
  }
  let out = '';
  for (const ch of digits) {
    if (out.length >= 4) break;
    const d = Number(ch);
    const i = out.length;
    if (i === 0 && d > 2) continue;
    if (i === 1 && out[0] === '2' && d > 3) continue;
    if (i === 2 && d > 5) continue;
    out += ch;
  }
  if (out.length <= 2) return out;
  return `${out.slice(0, 2)}:${out.slice(2)}`;
}

export function isValidStudyTime(value: string): boolean {
  return TIME_RE.test(value);
}

function rangeError(from: string, to: string, prefix: string, requireBoth: boolean): string {
  if (requireBoth && (!from || !to)) return `${prefix}укажите время «С» и «До»`;
  if (from && !isValidStudyTime(from))
    return `${prefix}время «С»: введите ЧЧ:ММ полностью (часы 00-23, минуты 00-59)`;
  if (to && !isValidStudyTime(to))
    return `${prefix}время «До»: введите ЧЧ:ММ полностью (часы 00-23, минуты 00-59)`;
  if (from && to && from >= to) return `${prefix}время «до» должно быть позже времени «с»`;
  return '';
}

const cap = (s: string) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

/** Текст ошибки или '' (используется в мастере, подборе, /try и плане одинаково). */
export function studyTimeError(time: StudyTime | null | undefined, requireDay = true): string {
  const t = time || EMPTY_STUDY_TIME;
  const mode = studyMode(t);
  if (mode === 'daily') {
    if (!requireDay && !t.time_from && !t.time_to) return '';
    return cap(rangeError(t.time_from, t.time_to, '', true));
  }
  if (mode === 'per_day') {
    const slots = t.slots || [];
    if (!slots.length) return requireDay ? 'Включите хотя бы один день' : '';
    for (const s of orderDays(slots.map((x) => x.day))) {
      const slot = slots.find((x) => x.day === s)!;
      const err = rangeError(slot.time_from, slot.time_to, `${dayLabel(s)}: `, true);
      if (err) return err;
    }
    return '';
  }
  if (requireDay && !t.days.length) return 'Выберите хотя бы один день';
  return cap(rangeError(t.time_from, t.time_to, '', false));
}

/** Значение из API (study_time) или из старых кодов study_schedule - для предзаполнения. */
export type StudyTimeInput = {
  mode?: string | null;
  days?: string[] | null;
  time_from?: string | null;
  time_to?: string | null;
  slots?: Array<{ day?: string | null; time_from?: string | null; time_to?: string | null }> | null;
};

export function studyTimeFromApi(
  studyTime?: StudyTimeInput | null,
  legacyCodes?: string[] | null,
): StudyTime {
  if (studyTime && studyTime.mode === 'per_day' && studyTime.slots?.length) {
    return syncDerived({
      mode: 'per_day',
      days: [],
      time_from: '',
      time_to: '',
      slots: studyTime.slots
        .filter((s) => s && DAY_ORDER.includes(String(s.day)))
        .map((s) => ({ day: String(s.day), time_from: s.time_from || '', time_to: s.time_to || '' })),
    });
  }
  if (studyTime && (studyTime.days?.length || studyTime.time_from || studyTime.time_to)) {
    const mode: StudyMode = studyTime.mode === 'daily' ? 'daily' : 'days';
    return normalizeStudyTime({
      mode,
      days: orderDays(studyTime.days || []),
      time_from: studyTime.time_from || '',
      time_to: studyTime.time_to || '',
    });
  }
  const codes = legacyCodes || [];
  if (!codes.length || codes.includes('flexible')) return { ...EMPTY_STUDY_TIME, slots: [] };
  const days = [
    ...(codes.includes('weekdays') ? WEEKDAYS : []),
    ...(codes.includes('weekends') ? WEEKENDS : []),
  ];
  const ranges: Array<[string, string]> = [];
  if (codes.includes('morning')) ranges.push(['09:00', '12:00']);
  if (codes.includes('afternoon')) ranges.push(['12:00', '17:00']);
  if (codes.includes('evening')) ranges.push(['17:00', '21:00']);
  return normalizeStudyTime({
    mode: 'days',
    days: orderDays(days),
    time_from: ranges.length ? ranges[0][0] : '',
    time_to: ranges.length ? ranges[ranges.length - 1][1] : '',
  });
}

/** Старые коды study_schedule из времени - для старых клиентов, фильтров и чата. */
export function studyScheduleCodes(time: StudyTime | null | undefined): string[] {
  if (!time) return [];
  const codes: string[] = [];
  if (time.days.some((d) => WEEKDAYS.includes(d))) codes.push('weekdays');
  if (time.days.some((d) => WEEKENDS.includes(d))) codes.push('weekends');
  if (time.time_from || time.time_to) {
    const mins = (t: string, dflt: number) => {
      if (!isValidStudyTime(t)) return dflt;
      const [h, m] = t.split(':').map(Number);
      return h * 60 + m;
    };
    const a = mins(time.time_from, 0);
    const b = mins(time.time_to, 24 * 60);
    if (a < 12 * 60 && b > 0) codes.push('morning');
    if (a < 17 * 60 && b > 12 * 60) codes.push('afternoon');
    if (b > 17 * 60) codes.push('evening');
  }
  return codes;
}

/** Тело для API: null, если ничего не выбрано. */
export function studyTimeToApi(time: StudyTime | null | undefined): StudyTime | null {
  if (!time) return null;
  const mode = studyMode(time);
  const ok = (v: string) => (isValidStudyTime(v) ? v : (null as unknown as string));
  if (mode === 'per_day') {
    const slots = orderDays((time.slots || []).map((s) => s.day))
      .map((d) => (time.slots || []).find((s) => s.day === d)!)
      .filter((s) => isValidStudyTime(s.time_from) && isValidStudyTime(s.time_to));
    if (!slots.length) return null;
    const derived = syncDerived({ mode, days: [], time_from: '', time_to: '', slots });
    return { mode, days: derived.days, time_from: derived.time_from, time_to: derived.time_to, slots };
  }
  const from = isValidStudyTime(time.time_from) ? time.time_from : '';
  const to = isValidStudyTime(time.time_to) ? time.time_to : '';
  if (mode === 'daily') {
    if (!from && !to) return null;
    return { mode, days: [...DAY_ORDER], time_from: ok(from), time_to: ok(to) };
  }
  if (!time.days.length && !from && !to) return null;
  return { mode, days: orderDays(time.days), time_from: ok(from), time_to: ok(to) };
}

function formatDays(days: string[]): string {
  const ordered = orderDays(days);
  if (ordered.length === 7) return 'Каждый день';
  if (ordered.length === 5 && WEEKDAYS.every((d) => ordered.includes(d))) return 'Будни';
  if (ordered.length === 2 && WEEKENDS.every((d) => ordered.includes(d))) return 'Выходные';
  return ordered.map(dayLabel).join(', ');
}

function formatRange(from?: string | null, to?: string | null): string {
  if (from && to) return `${from}${DASH}${to}`;
  if (from) return `после ${from}`;
  if (to) return `до ${to}`;
  return '';
}

/** «Ежедневно 18:00–20:00» / «Пн, Ср, Пт 18:00–20:00» / «Пн 17:00–18:00; Сб 10:00–12:00» / ''. */
export function formatStudyTime(time?: StudyTimeInput | null): string {
  if (!time) return '';
  if (time.mode === 'per_day' && time.slots?.length) {
    // Дни с одинаковым временем группируются: «Пн, Ср 18:00–20:00; Сб 10:00–12:00».
    const groups: Array<{ range: string; days: string[] }> = [];
    for (const d of orderDays(time.slots.map((s) => String(s.day)))) {
      const s = time.slots!.find((x) => x.day === d)!;
      const range = formatRange(s.time_from, s.time_to);
      const g = groups.find((x) => x.range === range);
      if (g) g.days.push(d);
      else groups.push({ range, days: [d] });
    }
    if (groups.length === 1 && groups[0].days.length === 7)
      return ['Ежедневно', groups[0].range].filter(Boolean).join(' ');
    return groups
      .map((g) => [g.days.map(dayLabel).join(', '), g.range].filter(Boolean).join(' '))
      .join('; ');
  }
  const range = formatRange(time.time_from, time.time_to);
  if (time.mode === 'daily') return ['Ежедневно', range].filter(Boolean).join(' ');
  const days = formatDays(time.days || []);
  return cap([days, range].filter(Boolean).join(' '));
}
